// What the run does next, when it is the *run's* turn to act rather than an
// army's.
//
// Two things happen between moves in a campaign: a beaten front turns over and
// a camp lands, and an objective completes and opens ground. Both were decided
// inside React effects — the first in `useSoloOpponent`, the second in
// `useRunDirector` — which meant the only way to play a run was to render one,
// and nothing headless could ever check that a run does not soft-lock.
//
// **The order between them is a rule, not a preference**, and it is the reason
// this is one function rather than two. They answer to the same event — you
// beat an army — and they used to do it on independent timers 500ms apart. The
// reveal won, and winning broke the boundary: a `reveal-region` naming a
// defeated side sweeps that army and recomputes the status, so by the time
// `next-stage` arrived the board was `playing` again and the reducer refused
// it. `GameStore.dispatch` answers `false` and swallows it, so the camp simply
// never spawned and nothing said why. A front turning over goes first, always.
//
// Everything here is a *decision*. The hooks keep their timing, their camera
// and their error reporting; what they no longer keep is a second opinion about
// what should happen.

import type { Side } from "src/engine/rules/board";
import { growthSeed } from "src/engine/rules/growth";
import {
  LINE_COUNT,
  type MaterialLine,
  METAL,
  STONE
} from "src/engine/rules/materials";
import { DEPOSIT_STREAM, DUE_OFFSET, rollDeposits } from "./deposits";
import { growthRow, MAPGEN_GROWTH_STREAM, mapGenOf } from "src/engine/rules/mapgen";
import { factionForStage } from "src/engine/rules/campaign/factions";
import { KING_FACTORY } from "src/engine/rules/position";
import type { ZoneRow } from "src/engine/rules/zones";
import { RULES } from "src/engine/rules/rules";
import { streamFor } from "./random";
import type { GameState } from "src/engine/rules/commands";
import type { FloorCommand } from "src/engine/rules/turns";
import { type DepositRow, depositContents, unitLine } from "src/engine/rules/industry/state";
import { type Level, pacedStage } from "./difficulty";
import { spawnHoldsNext, waveIn } from "src/engine/rules/campaign/pending";
import {
  arrivingSide,
  chooseWave,
  hasKing,
  OFFERS,
  stagesCleared,
  stageTurnsOver,
  type Wave,
  type WaveOffer,
  waveOptions,
  PAIR_OFFER_STREAM,
  PAIR_STREAM
} from "./waves";

/**
 * What the run itself does, and **never what a player does** (§29 U2).
 *
 * Every arm's command is a `FloorCommand`, so a step carrying something Undo
 * would take back does not compile. The director dispatches these on whoever's
 * clock it is, with no owner: classed as a player's action they sat above the
 * undo floor, and taking back the board's expansion bought infinite moves.
 */
export type RunStep =
  | {
      kind: "stage";
      /** The camp landing, as the command that lands it. */
      command: Extract<FloorCommand, { type: "next-stage" }>;
      /** Which front this is, counting from one. For the message when it fails. */
      stage: number;
    }
  | {
      kind: "reveal";
      command: Extract<FloorCommand, { type: "reveal-region" }>;
      /** The ground it opens, for the camera to lead with. */
      cells: readonly number[];
    }
  /**
   * A front that has to turn over and cannot.
   *
   * Its own answer rather than `null`, because the two are opposite: `null` is
   * "nothing to do, carry on playing", and this is "the board is stuck and
   * every turn from here is wasted". It used to be a `console.error` inside an
   * effect — invisible to anything that was not a person watching a browser.
   */
  | { kind: "stalled"; why: string }
  /**
   * A front closing *without* landing a camp (§26 F10, `spawnHoldsNext`).
   *
   * Under the spawn clock, beating one of several enemies sweeps that army and
   * hands the clock back — the schedule keeps the next landing, or two live
   * factions would be permanent: beat one and its replacement arrives at once,
   * forever. The command is `clear-front` with the schedule's own remainder.
   */
  | { kind: "clear"; command: Extract<FloorCommand, { type: "clear-front" }> }
  | null;

/**
 * The next thing the run itself should do, or null to let an army move.
 *
 * **It reads the state and nothing else.** It used to take the command log too,
 * for the one fact about a run that lived in the command sequence — how many
 * fronts had been cleared — and that turned out to be the bug rather than the
 * design: a run is saved as a snapshot, so the log is empty after a Continue
 * and the count came back as nought. `GameState.stage` is where the round lives
 * now, and the parameter went with it rather than staying as a dead argument
 * inviting somebody to re-derive from it.
 */
export function runStep(
  state: GameState,
  playerSide: Side,
  difficulty: Level
): RunStep {
  if (!state.run) return null;
  // Order, and it is the rule this file exists for.
  if (stageTurnsOver(state, playerSide)) {
    // Under the spawn clock, a beaten front with another enemy still standing
    // closes without landing — the schedule owns the next camp.
    const hold = spawnHoldsNext(state);
    if (hold !== null) {
      return {
        kind: "clear",
        command: { type: "clear-front", side: state.position.turn, delay: hold }
      };
    }
    return stageStep(state, difficulty);
  }
  const reveal = revealStep(state);
  return reveal ? { kind: "reveal", ...reveal } : null;
}

/**
 * The camp that lands when a front turns over.
 *
 * Split from `runStep` because building a wave is not cheap — `nextWave` tries
 * several orientations and anchors against a board it clones — and the run
 * director re-runs on every state change. It asks `stageTurnsOver` and stops;
 * only the driver that actually lands the camp pays for one.
 */
export function stageStep(
  state: GameState,
  difficulty: Level
): RunStep {
  const branch = stageOffers(state, difficulty);
  if (!branch) {
    return {
      kind: "stalled",
      why: `stage ${stagesCleared(state) + 1}: no camp could be placed, so the front cannot turn over`
    };
  }
  // **Nobody to ask, so the evaluation answers** (§19.6 hazard 4). Watch mode
  // and the headless rig have no player; `chooseWave` takes the offer whose
  // board comes out nearest to even, which is also the balance instrument —
  // an automatic chooser that always takes offer 0 means the offers are not a
  // choice.
  // **A board with no camp on it is the case this used to throw on.**
  //
  // `chooseWave` answers `offers[0].wave` for a list of one or fewer, which on
  // an *empty* list is a read off `undefined`. Thrown from inside the turn
  // loop it takes the driver with it, and the stage never turns over.
  const choice =
    branch.offers.length > 0
      ? chooseWave(state, branch.offers, state.run?.side ?? 0)
      : branch.grow;
  if (!choice) {
    return {
      kind: "stalled",
      why: `stage ${branch.stage}: nothing could be landed — no camp fits and the board cannot be regrown`
    };
  }
  return {
    kind: "stage",
    stage: branch.stage,
    command: stageCommand(state, branch, choice)
  };
}

/**
 * The camps a cleared front could open onto, and what a run needs to land one.
 *
 * Split from `stageStep` because 19b hands the choice to the player: the panel
 * needs the offers *without* one being taken, and the driver needs the same
 * `stage` and `arriving` to build the command afterwards. `stageStep` is now
 * this plus `chooseWave`, so the automatic path and the offered path cannot
 * generate different camps.
 *
 * **Null is "no camp could be placed"**, which is the front unable to turn
 * over — the loud failure, not an empty choice.
 */
export function stageOffers(
  state: GameState,
  difficulty: Level
): StageOffers | null {
  const stage = stagesCleared(state) + 1;
  // Never one of ours, and never an army still fighting — see `arrivingSide`.
  // This was a second copy of the counter it replaced, and a counter that knows
  // nothing about which sides exist hands the next camp to your own ally.
  const arriving = arrivingSide(state, stage);
  const offers = waveOptions(state, arriving, stage, difficulty).filter(
    (offer) => hasKing(offer.wave.pieces)
  );
  // **A second front, past stage eight, sometimes.** Its own stream, or the
  // draw would shift every existing seed's camps as a side effect. The second
  // flag has to be genuinely different: `arrivingSide` skips what is ours and
  // what is living, but asked twice about the same board it can answer the
  // same colour — and two camps under one flag is one camp in two pieces.
  //
  // **Off its own offer stream, or it is the first camp again.** Both camps are
  // built from the same seed, the same stage and the same board, so sharing
  // `OFFER_STREAM` meant sharing every roll in `buildWave` — formation, facing
  // and anchor alike. The second camp landed on the first one's squares, the
  // reducer skips a piece whose square is taken, and what arrived was an army
  // of nothing under a flag that had been spent. `PAIR_OFFER_STREAM` is what
  // makes the two disagree about *where*, which is the whole of what a second
  // front is.
  let pair: Wave | undefined;
  if (pacedStage(stage) >= RULES.pairFromStage && offers.length > 0) {
    const roll = streamFor(state.seed, stage, PAIR_STREAM);
    const second = arrivingSide(state, stage + 1);
    if (roll() < RULES.pairChance && second !== arriving) {
      const built = waveOptions(
        state,
        second,
        stage,
        difficulty,
        OFFERS,
        PAIR_OFFER_STREAM
      ).filter((offer) => hasKing(offer.wave.pieces));
      // **Off the ground the default front stands on.** Two streams disagree
      // about *where* only by chance: a small formation pool hands both the
      // same camp, the same best anchor wins both, and `stageCommand` drops
      // the pair for overlapping. Filtered here, so a chance overlap costs
      // one candidate rather than the whole second front.
      const taken = new Set(chooseWave(state, offers, state.run?.side ?? 0).cells);
      const apart = built.filter((offer) => !offer.wave.cells.some((sq) => taken.has(sq)));
      if (apart.length > 0) pair = chooseWave(state, apart, state.run?.side ?? 0);
    }
  }

  // **Ground coming back, when there is nowhere to put a front.** Null there
  // would leave the front jammed: the stage never turns over and the board
  // sits in checkmate. Offered only in that case: a board that can still hold
  // a front should be fighting on one.
  const grow = offers.length === 0 ? (planGrowth(state) ?? undefined) : undefined;

  if (offers.length === 0 && !grow) return null;
  return { stage, arriving, offers, pair, grow };
}

/** A front's offers, with everything the command that lands one needs. */
export interface StageOffers {
  stage: number;
  /** The flag the arriving camp flies. See `arrivingSide`. */
  arriving: Side;
  /** One or two. **Never assume a pair** — a cramped board offers one. */
  offers: WaveOffer[];
  /**
   * A **second camp**, opening the front on two sides at once.
   *
   * Past `RULES.pairFromStage`, sometimes. Not a harder camp — a second
   * *front*, which changes the shape of a turn rather than its difficulty:
   * there is one behind you as well as in front, and the army that was
   * marching has to be in two places.
   *
   * Beside the offers rather than among them because it is not a choice — the
   * player picks which camp lands and this one lands too. Absent whenever the
   * roll says no, whenever the stage is too early, and whenever a second free
   * flag could not be found, which is the case that would otherwise put two
   * camps under one colour.
   */
  pair?: Wave;
  /**
   * The board coming *back*, offered when no camp will fit — a map with
   * nowhere to put a front used to leave the run stalled.
   *
   * Offered only when there is no camp, because it is a *recovery* rather than
   * a choice: a board that can still hold a front should be fighting on one.
   */
  grow?: GrowthPlan;
}

/** Ground coming back, planned against the board it will arrive on. */
export interface GrowthPlan {
  /** Where it starts. `growthSeed`: the edge nearest your army. */
  seed: number;
  /** How far it carries, which is also how many turns it takes to lay. */
  depth: number;
}

/**
 * Plan a regrowth, or null when there is nowhere to put one.
 *
 * Nowhere means a board with no frontier at all, which takes a board with no
 * squares — so this is very nearly always available, and deliberately: it is
 * the thing that stops a run being stuck.
 */
export function planGrowth(state: GameState): GrowthPlan | null {
  const side = state.run?.side ?? state.position.turn;
  const seed = growthSeed(state.position, side);
  return seed < 0 ? null : { seed, depth: RULES.growDepth };
}

/**
 * The command that lands a chosen camp.
 *
 * **One builder, because there are now three callers** — the headless rig, the
 * driver's automatic path and the player's pick — and a camp that arrives
 * differently depending on who chose it is the sort of divergence nothing
 * would catch until a replay disagreed.
 */
export function stageCommand(
  state: GameState,
  branch: StageOffers,
  /**
   * The camp that was chosen, or ground coming back instead.
   *
   * A union rather than a second builder, because "one builder" is the rule
   * this function exists for: a stage that advanced differently depending on
   * who chose it is the divergence nothing catches until a replay disagrees.
   */
  choice: Wave | GrowthPlan
): Extract<FloorCommand, { type: "next-stage" }> {
  // Ground coming back: no army, no ground of its own to name — the seed and
  // its counter are the whole of it, and the tick lays the rest.
  if ("depth" in choice) {
    return {
      type: "next-stage",
      side: state.position.turn,
      pieces: [],
      grow: { seed: choice.seed, depth: choice.depth },
      gambit: false
    };
  }
  const wave = choice;
  // **A front may open on two sides at once past stage eight**, and the pair
  // lands through the *same* command rather than a second one: two commands
  // would be two stage boundaries, two Gambits and two chances for the second
  // to be refused because the first stopped the board being a mate.
  //
  // Merged rather than interleaved — cells, pieces and promotion simply
  // concatenate, since the reducer places each against the ground it has by
  // then and skips anything that lands in a hole.
  // **Two fronts, or one — never one camp standing inside another.** The pair
  // is built against the board as it is now, and so is every offer, so neither
  // knows where the other went; the player then picks which camp lands, which
  // is a choice the pair was generated before. Where the two want the same
  // ground the reducer would place whichever it reached first and skip the
  // rest in silence — and the piece it skips is as likely as any to be the
  // king, which is a front that ends itself on the turn it opens.
  //
  // Disjoint *ground* rather than disjoint pieces, because a camp's pieces
  // stand on its own cells: no shared square means nothing to drop, and it is
  // one comparison instead of a rule about which camp wins a contested one.
  // Dropping the second front is the honest answer — a front that arrives half
  // swallowed is not the mechanic, and the next stage will offer one again.
  const taken = new Set(wave.cells);
  const pair = branch.pair?.cells.some((sq) => taken.has(sq)) ? undefined : branch.pair;
  const both = pair ? [wave, pair] : [wave];
  const sides = both.map((one) => one.side);
  const field = campField(state, wave, both);
  const cells = both.flatMap((one) => one.cells);

  return {
    type: "next-stage",
    side: state.position.turn,
    cells,
    // **A generated map grows by zones** (§31 D3): one lobe off the config's
    // growth pool, beside the camp. Absent on an authored board.
    ...stageZones(state, wave.stage, field.cells ?? cells),
    sides: Math.max(state.position.sides, ...sides.map((side) => side + 1)),
    pieces: both.flatMap((one) => one.pieces),
    promotion: both.flatMap((one) => one.promotion),
    // And whatever industry the camp brought. Absent on a generated camp,
    // which has none — the fields carry an authored room's seam, and the
    // reducer skips an absent one rather than placing an empty list.
    heaps: both.flatMap((one) => one.heaps ?? []),
    // A field on the camp's own ground, away from the army landing on it (§26
    // F3). Here rather than in `buildWave` because `stageCommand` is the one
    // builder: a front that arrived with different ore depending on who chose
    // it is the divergence nothing catches until a replay disagrees.
    //
    // **The clearance is what makes a front fight over it.** A camp's field
    // lands on the approach between the two forces rather than under its own
    // pawns, so working it means standing diggers on contested ground — where
    // the aggression term already sends the enemy. No denial term, no
    // industrial camp: the geometry is the mechanism (§26 F3, decided).
    ...field,
    metal: both.flatMap((one) => one.metal ?? []),
    facing: both.flatMap((one) => one.facing ?? []),
    structures: both.flatMap((one) => one.structures ?? []),
    // **Factions in random order** (§31 G3): the run's shuffle, indexed by
    // the stage. Absent while it answers the factory army, the only one.
    ...campKings(state.seed, wave.stage, sides),
    // The one place a Gambit fires in single player, and it runs
    // unilaterally: beating an army is when you have earned a run of your
    // factory, and it is when you most need one — the new camp arrives in
    // formation while your network is wherever the last fight left it.
    //
    // **Earned by beating a front, never by the clock** (§26 F10). A mate now
    // and a `clear-front` camp coming due are both fronts somebody beat; a
    // spawn-clock landing arrives on a board where nobody was, and a free run
    // of every economy each `spawnEvery` turns would be the clock paying the
    // player for falling behind.
    gambit:
      state.status === "checkmate" ||
      state.status === "stalemate" ||
      waveIn(state) === 0
  };
}

/** `next-stage.kings` for the arriving sides, or nothing for the factory army. */
function campKings(
  seed: number,
  stage: number,
  sides: readonly Side[]
): { kings?: [Side, number][] } {
  const kind = factionForStage(seed, stage);
  return kind === KING_FACTORY ? {} : { kings: sides.map((side) => [side, kind]) };
}

/**
 * The zone lobe a stage brings on a generated map (§31 D3), or nothing. Its
 * own stream per stage; the reducer grows the contents off the seed.
 */
function stageZones(
  state: GameState,
  stage: number,
  landing: readonly number[]
): { zones?: readonly ZoneRow[] } {
  const config = mapGenOf(state.map);
  if (!config) return {};
  const row = growthRow(
    state.position,
    state.industry,
    config,
    landing,
    streamFor(state.seed, stage, MAPGEN_GROWTH_STREAM)
  );
  return row ? { zones: [row] } : {};
}

/**
 * The field a region reveal brings, or nothing.
 *
 * **Every board-opening event in a run is a spawn opportunity**, and a reveal
 * is the commonest of them — a run opens the tutorial's camp region before it
 * has ever fought anybody, and before this that reveal brought no ore at all.
 *
 * **Its own salt inside the deposit block**, one per region: a stage can open
 * several regions, and `streamFor(seed, stage, DEPOSIT_STREAM)` is one stream,
 * so two of them would generate the same field twice.
 *
 * **Keep-away is every piece on the board, whoever owns it.** A reveal is not
 * a camp landing: there is no arriving faction whose approach the field should
 * be on, and what it must not be is under somebody's feet.
 */
function revealField(
  state: GameState,
  region: { cells: readonly number[] },
  index: number
): { deposits?: readonly DepositRow[]; cells?: number[] } {
  const salt = 1 + index;
  const round = stagesCleared(state);
  if (!fieldIsDue(state, streamFor(state.seed, round, DEPOSIT_STREAM + DUE_OFFSET + salt))) {
    return {};
  }
  const plan = rollDeposits(
    state.seed,
    round,
    region.cells,
    [...state.position.board.keys()],
    { camp: true, board: state.position, salt, line: scarcestLine(state) }
  );
  if (plan.rows.length === 0) return {};
  return { deposits: plan.rows, cells: plan.ground.length > 0 ? plan.ground : undefined };
}

/**
 * Un-mined units left, **per line** — what the next field is chosen against,
 * and what the pity rule reads.
 *
 * Iron feeds pieces and stone feeds buildings, so a board short of one is a
 * board with half its economy stalled. Spawning whatever is scarce keeps both
 * live and closes the contention loop on itself: exhausting a line is what
 * summons its own resupply, and a player who has just belted a field of cobble
 * into a warehouse gets iron next rather than a coin toss.
 *
 * Read where the command is built, like the pity rule, and the answer rides
 * the command — so a replay puts the same line back without re-deriving it.
 */
export function oreLeftByLine(state: GameState): number[] {
  const out = new Array<number>(LINE_COUNT).fill(0);
  // A mixed tile counts each half to its own line (§31 D1).
  for (const [sq] of state.industry.deposit) {
    for (const { unit, count } of depositContents(state.industry, sq)) out[unitLine(unit)] += count;
  }
  return out;
}

/**
 * The line the board is shortest of — **metal on a tie**.
 *
 * The opening, where both are nought, is the commonest tie there is, and
 * pieces are what a run is played with: a stone-first opening is a run that
 * cannot build one. So the first field is always iron, and the second is stone
 * because the first made it the scarce line.
 */
export function scarcestLine(state: GameState): MaterialLine {
  const left = oreLeftByLine(state);
  return left[STONE] < left[METAL] ? STONE : METAL;
}

/**
 * Does this board-opening bring a field with it?
 *
 * **Every opening is an opportunity, and the roll is texture rather than the
 * rule.** Guaranteed when a workhorse line is running dry — the *scarcer* of
 * the two under `depositLowWater`, never the total: metal is always carved
 * first, so a total would let a run sit on a heap of iron and no stone for
 * ever, which is the playtest's own complaint made worse. A board with no ore
 * at all is nought on both lines and so is due by the same reading — that is
 * what puts the first field on the tutorial the moment its dig-in objective
 * opens the camp's region. Specials never count: they are not what anything
 * is built out of.
 */
export function fieldIsDue(state: GameState, roll: () => number): boolean {
  const left = oreLeftByLine(state);
  if (Math.min(left[METAL], left[STONE]) < RULES.depositLowWater) return true;
  return roll() < RULES.depositSpawnChance;
}

/** The field a landing camp brings, or nothing. */
function campField(
  state: GameState,
  wave: Wave,
  both: readonly Wave[]
): { deposits?: readonly DepositRow[]; cells?: number[] } {
  // **The due-roll gets its own salt**, or the generator's first draw is the
  // number the roll just spent — see `DUE_OFFSET`.
  const due = fieldIsDue(state, streamFor(state.seed, wave.stage, DEPOSIT_STREAM + DUE_OFFSET));
  if (!due) return {};
  const cells = both.flatMap((one) => one.cells);
  const plan = rollDeposits(
    state.seed,
    wave.stage,
    cells,
    both.flatMap((one) => one.pieces.map(([sq]) => sq)),
    { camp: true, board: state.position, line: scarcestLine(state) }
  );
  if (plan.rows.length === 0) return {};
  // The lobe rides the command's own `cells`, which the reducer already opens
  // through `setCells` — never a second way to add ground.
  return { deposits: plan.rows, cells: [...cells, ...plan.ground] };
}

/**
 * The ground an objective has earned and nobody has opened yet.
 *
 * **Never call this while a front is turning over** — `runStep` is the ordering
 * and the reason for it. Cheap, so the director may ask on every render.
 */
export function revealStep(
  state: GameState
): { command: Extract<FloorCommand, { type: "reveal-region" }>; cells: readonly number[] } | null {
  const run = state.run;
  const level = run?.level;
  if (!run || !level) return null;

  // The first region that has been earned and is not open yet. Objectives
  // complete in order, so there is never more than one — but taking the first
  // rather than all of them keeps the camera on one thing at a time.
  for (const objective of level.objectives) {
    if (!run.objectives.find((one) => one.id === objective.id)?.done) continue;

    for (const id of objective.reveals) {
      if (run.revealed.includes(id)) continue;
      const index = level.regions.findIndex((entry) => entry.id === id);
      const region = level.regions[index];
      if (!region) continue;
      const field = revealField(state, region, index);
      return {
        cells: [...region.cells, ...(field.cells ?? [])],
        command: {
          type: "reveal-region",
          region: id,
          // A `defeat` objective sweeps the army it names, which is what drops
          // its king's crown. Any other kind opens ground and leaves whoever is
          // standing on the board alone.
          defeated: objective.kind === "defeat" ? objective.side : undefined,
          ...field
        }
      };
    }
  }

  return null;
}
